import React, { useState } from 'react';
import { FaPaperPlane, FaMobileAlt, FaSearch, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import {
  broadcastNotification,
  lookupFcmTokens,
  sendTestFcm,
} from '../../../api/services/adminService';
import { SectionHeader, Alert } from './AdminUI';
import './Notifications.css';

const ROLE_OPTIONS = [
  { value: '', label: 'All active users' },
  { value: 'LANDLORD', label: 'Landlords' },
  { value: 'BNB_HOST', label: 'Airbnb hosts' },
  { value: 'AGENT', label: 'Agents' },
  { value: 'TENANT', label: 'Tenants' },
  { value: 'ADMIN', label: 'Admins' },
];

const Notifications = () => {
  // ---- Broadcast state ----
  const [broadcast, setBroadcast] = useState({ title: '', body: '', targetRole: '', clickUrl: '' });
  const [sending, setSending] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState(null);

  // ---- FCM diagnostics state ----
  const [email, setEmail] = useState('');
  const [tokens, setTokens] = useState(null);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensError, setTokensError] = useState(null);

  const [testFcm, setTestFcm] = useState({ token: '', title: 'TenantScore test', message: 'This is a test push notification.' });
  const [sendingFcm, setSendingFcm] = useState(false);
  const [fcmMsg, setFcmMsg] = useState(null);

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcast.title.trim() || !broadcast.body.trim()) {
      setBroadcastMsg({ type: 'error', text: 'Title and body are required.' });
      return;
    }
    setSending(true);
    setBroadcastMsg(null);
    try {
      const payload = {
        title: broadcast.title.trim(),
        body: broadcast.body.trim(),
      };
      if (broadcast.targetRole) payload.targetRole = broadcast.targetRole;
      if (broadcast.clickUrl.trim()) payload.clickUrl = broadcast.clickUrl.trim();
      const res = await broadcastNotification(payload);
      const count = res?.data?.recipientCount ?? res?.data?.recipientCount;
      setBroadcastMsg({ type: 'success', text: res?.message || `Notification broadcast to ${count ?? ''} users.` });
      setBroadcast({ title: '', body: '', targetRole: '', clickUrl: '' });
    } catch (err) {
      setBroadcastMsg({ type: 'error', text: err?.message || 'Broadcast failed.' });
    } finally {
      setSending(false);
    }
  };

  const handleLookupTokens = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setTokensLoading(true);
    setTokensError(null);
    setTokens(null);
    try {
      const res = await lookupFcmTokens(email.trim());
      setTokens(res?.data || []);
    } catch (err) {
      setTokensError(err?.message || 'Failed to look up FCM tokens.');
    } finally {
      setTokensLoading(false);
    }
  };

  const handleSendTestFcm = async (e) => {
    e.preventDefault();
    if (!testFcm.token.trim() || !testFcm.message.trim()) {
      setFcmMsg({ type: 'error', text: 'Token and message are required.' });
      return;
    }
    setSendingFcm(true);
    setFcmMsg(null);
    try {
      const res = await sendTestFcm({
        token: testFcm.token.trim(),
        title: testFcm.title.trim() || 'TenantScore test',
        message: testFcm.message.trim(),
      });
      setFcmMsg({ type: 'success', text: res?.message || 'Test notification sent.' });
    } catch (err) {
      setFcmMsg({ type: 'error', text: err?.message || 'Failed to send test notification.' });
    } finally {
      setSendingFcm(false);
    }
  };

  return (
    <div className="admin-notifications">
      <SectionHeader
        title="Notifications"
        subtitle="Broadcast platform-wide announcements and diagnose push notification delivery."
      />

      <div className="admin-grid admin-grid-2">
        {/* ---------- Broadcast ---------- */}
        <div className="admin-card notif-card">
          <div className="notif-card-head">
            <div className="notif-card-icon"><FaPaperPlane /></div>
            <div>
              <h3 className="admin-card-title">Broadcast notification</h3>
              <p className="admin-card-subtitle">Send a push notification to all users or a specific role.</p>
            </div>
          </div>

          {broadcastMsg && (
            <Alert type={broadcastMsg.type === 'success' ? 'success' : 'error'} onClose={() => setBroadcastMsg(null)}>
              {broadcastMsg.text}
            </Alert>
          )}

          <form className="notif-form" onSubmit={handleBroadcast}>
            <label className="notif-field">
              <span>Title</span>
              <input
                type="text"
                value={broadcast.title}
                onChange={(e) => setBroadcast((b) => ({ ...b, title: e.target.value }))}
                placeholder="e.g. Scheduled maintenance"
                maxLength={120}
                required
              />
            </label>

            <label className="notif-field">
              <span>Message</span>
              <textarea
                value={broadcast.body}
                onChange={(e) => setBroadcast((b) => ({ ...b, body: e.target.value }))}
                placeholder="Notification body…"
                rows={3}
                maxLength={500}
                required
              />
            </label>

            <label className="notif-field">
              <span>Audience</span>
              <select value={broadcast.targetRole} onChange={(e) => setBroadcast((b) => ({ ...b, targetRole: e.target.value }))}>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>

            <label className="notif-field">
              <span>Click URL (optional)</span>
              <input
                type="text"
                value={broadcast.clickUrl}
                onChange={(e) => setBroadcast((b) => ({ ...b, clickUrl: e.target.value }))}
                placeholder="/tenant/dashboard"
              />
            </label>

            <button type="submit" className="admin-btn admin-btn-primary" disabled={sending}>
              <FaPaperPlane /> {sending ? 'Sending…' : 'Send broadcast'}
            </button>
          </form>
        </div>

        {/* ---------- FCM diagnostics ---------- */}
        <div className="admin-card notif-card">
          <div className="notif-card-head">
            <div className="notif-card-icon notif-card-icon-alt"><FaMobileAlt /></div>
            <div>
              <h3 className="admin-card-title">Push diagnostics</h3>
              <p className="admin-card-subtitle">Look up a user's device tokens and send a test push.</p>
            </div>
          </div>

          <form className="notif-form" onSubmit={handleLookupTokens}>
            <label className="notif-field">
              <span>User email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
            </label>
            <button type="submit" className="admin-btn admin-btn-outline" disabled={tokensLoading}>
              <FaSearch /> {tokensLoading ? 'Searching…' : 'Look up tokens'}
            </button>
          </form>

          {tokensError && <Alert type="error">{tokensError}</Alert>}

          {tokens !== null && (
            <div className="notif-tokens-result">
              <div className="notif-tokens-summary">
                {tokens.length > 0 ? (
                  <span className="admin-badge admin-badge-success"><FaCheckCircle /> {tokens.length} token{tokens.length !== 1 ? 's' : ''}</span>
                ) : (
                  <span className="admin-badge admin-badge-warning"><FaExclamationTriangle /> No active tokens</span>
                )}
              </div>
              {tokens.length > 0 && (
                <ul className="notif-tokens-list">
                  {tokens.map((t, i) => (
                    <li key={t.id || i}>
                      <code>{t.token ? `${t.token.slice(0, 24)}…` : '—'}</code>
                      <span className="notif-token-meta">
                        {t.deviceType || t.platform || 'device'} · {t.active === false ? 'inactive' : 'active'}
                      </span>
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost"
                        onClick={() => setTestFcm((s) => ({ ...s, token: t.token || '' }))}
                      >
                        Use
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="notif-divider" />

          <form className="notif-form" onSubmit={handleSendTestFcm}>
            <label className="notif-field">
              <span>Device token</span>
              <input
                type="text"
                value={testFcm.token}
                onChange={(e) => setTestFcm((s) => ({ ...s, token: e.target.value }))}
                placeholder="Paste FCM token"
                required
              />
            </label>
            <label className="notif-field">
              <span>Title</span>
              <input
                type="text"
                value={testFcm.title}
                onChange={(e) => setTestFcm((s) => ({ ...s, title: e.target.value }))}
                placeholder="Notification title"
              />
            </label>
            <label className="notif-field">
              <span>Message</span>
              <textarea
                value={testFcm.message}
                onChange={(e) => setTestFcm((s) => ({ ...s, message: e.target.value }))}
                rows={2}
                required
              />
            </label>

            {fcmMsg && (
              <Alert type={fcmMsg.type === 'success' ? 'success' : 'error'} onClose={() => setFcmMsg(null)}>
                {fcmMsg.text}
              </Alert>
            )}

            <button type="submit" className="admin-btn admin-btn-primary" disabled={sendingFcm}>
              <FaMobileAlt /> {sendingFcm ? 'Sending…' : 'Send test push'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
